import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { authControllerRefreshUserToken } from '@/api/auth/auth'
import { router } from '@/app/router/instance'
import { GoogleCallbackPage } from '@/pages/google-callback'
import { consumeGoogleAuthRedirect, isSafeRedirectPath, setAccessToken } from '@/shared/lib/auth'
import { SignInRoute, WorkflowRoute } from '@/shared/routing'

const searchSchemaValidation = z.object({
  status: z.enum(['authenticated', 'signup_required', 'error']).optional(),
  email: z.string().optional(),
})

export const Route = createFileRoute('/google-callback')({
  component: RouteComponent,
  validateSearch: (search) => searchSchemaValidation.parse(search),
  loaderDeps: ({ search: { status } }) => ({ status }),
  loader: async ({ deps: { status } }) => {
    const redirectPath = consumeGoogleAuthRedirect()

    if (status === 'authenticated') {
      const { accessToken } = await authControllerRefreshUserToken()
      setAccessToken(accessToken)

      // Google's account chooser stays behind us in browser history as a real
      // cached page. Push a sentinel so the next Back press is a same-page
      // popstate we can intercept, and re-initiate a fresh login instead of
      // resubmitting Google's already-consumed state. Disarmed on the next
      // in-app navigation so it never hijacks an ordinary Back press later.
      window.history.pushState(null, '', window.location.href)
      const onPopState = () => {
        window.removeEventListener('popstate', onPopState)
        unsubscribe()
        window.location.href = `${import.meta.env.PUBLIC_API_URL}/auth/google`
      }
      const unsubscribe = router.subscribe('onResolved', () => {
        window.removeEventListener('popstate', onPopState)
      })
      window.addEventListener('popstate', onPopState)

      if (isSafeRedirectPath(redirectPath)) {
        throw redirect({ href: redirectPath, replace: true })
      }
      throw redirect({ to: WorkflowRoute.to, replace: true })
    }

    if (status !== 'signup_required') {
      throw redirect({ to: SignInRoute.to, replace: true })
    }
  },
  onError: () => {
    throw redirect({ to: SignInRoute.to, replace: true })
  },
})

export const GoogleCallbackRoute = Route

function RouteComponent() {
  const { email } = Route.useSearch()

  return <GoogleCallbackPage email={email} />
}
